/**
 * useSessionRefresh — silent access-token refresh hook.
 *
 * Mount ONCE inside AuthLayout (authenticated area only).
 *
 * Behaviour:
 * 1. Reads the current access token's exp and schedules a timer to fire
 *    5 minutes before expiry.
 * 2. On timer fire: calls loginApi.refresh() and stores new tokens in the
 *    same storage the user originally logged in to.
 * 3. On visibilitychange (tab regains focus): immediately re-checks expiry.
 *    If within 5 min of expiry → refresh now. If already expired → logout.
 * 4. On storage event (another tab refreshed): reschedules from the new token.
 * 5. On refresh failure (any error including 401) → clear tokens + emit
 *    SESSION_EXPIRED (deferred to authInterceptor / sessionEvents path).
 * 6. Module-level single-flight: concurrent timer fires or tab events share
 *    one in-progress refresh promise and do not double-refresh.
 */

import { useEffect, useRef } from 'react';
import {
  getStoredToken,
  getStoredRefreshToken,
  getTokenStorage,
  storeTokens,
  clearStoredTokens,
} from '../../login/api/loginApi';
import { loginApi } from '../../login/api/loginApi';
import { decodeJwtExp, isTokenExpired } from './jwt';
import { sessionEvents } from './sessionEvents';

/** 5 minutes before expiry */
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/** Module-level single-flight: only one refresh in-flight at a time */
let inflight: Promise<void> | null = null;

/**
 * Calculates how many milliseconds until we should fire the refresh timer.
 * Returns 0 if already within the 5-min window or expired.
 */
function msUntilRefresh(token: string): number {
  const exp = decodeJwtExp(token);
  if (exp === null) return 0;
  const fireAt = exp * 1000 - REFRESH_BEFORE_EXPIRY_MS;
  return Math.max(0, fireAt - Date.now());
}

/**
 * Performs a silent refresh. Protected by a single-flight promise so multiple
 * callers (timer + visibilitychange + storage event) share one in-progress
 * request.
 *
 * On success: stores new tokens in the same storage.
 * On failure: clears tokens and emits SESSION_EXPIRED.
 */
async function doRefresh(): Promise<void> {
  if (inflight) return inflight;

  inflight = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      clearStoredTokens();
      sessionEvents.emit({
        type: 'SESSION_EXPIRED',
        returnUrl: typeof window !== 'undefined' ? window.location.pathname : '/',
      });
      return;
    }

    try {
      const data = await loginApi.refresh(refreshToken);
      const storage = getTokenStorage();
      storeTokens(data.accessToken, data.refreshToken, storage);
    } catch {
      // Any refresh failure (including 401/AUTH-401-002/004) → logout
      clearStoredTokens();
      sessionEvents.emit({
        type: 'SESSION_EXPIRED',
        returnUrl: typeof window !== 'undefined' ? window.location.pathname : '/',
      });
    }
  })();

  // Clear single-flight slot when done (success or failure)
  inflight.finally(() => {
    inflight = null;
  });

  return inflight;
}

export function useSessionRefresh(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Clear any running timer */
  function clearTimer() {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  /** Schedule the next refresh timer based on the current token */
  function scheduleNext() {
    clearTimer();
    const token = getStoredToken();
    if (!token) return;

    const delay = msUntilRefresh(token);

    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      const currentToken = getStoredToken();
      if (!currentToken) return;
      // If already expired by the time the timer fires, go straight to logout
      if (isTokenExpired(currentToken)) {
        clearStoredTokens();
        sessionEvents.emit({
          type: 'SESSION_EXPIRED',
          returnUrl: typeof window !== 'undefined' ? window.location.pathname : '/',
        });
        return;
      }
      await doRefresh();
      // After successful refresh, reschedule for the new token
      scheduleNext();
    }, delay);
  }

  useEffect(() => {
    // Initial schedule on mount
    scheduleNext();

    /** Handle tab becoming visible: re-check expiry */
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const token = getStoredToken();
      if (!token) return;

      if (isTokenExpired(token)) {
        clearStoredTokens();
        sessionEvents.emit({
          type: 'SESSION_EXPIRED',
          returnUrl: window.location.pathname,
        });
        return;
      }

      // Within 5 min of expiry → refresh now, then reschedule
      const delay = msUntilRefresh(token);
      if (delay === 0) {
        await doRefresh();
      }
      // Reschedule regardless (timer may have fired while tab was hidden)
      scheduleNext();
    };

    /**
     * Handle another tab refreshing tokens: reschedule from the new token.
     * The storage event fires when a different tab writes to localStorage.
     */
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        scheduleNext();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
